import { useState } from 'react'
import { AssessmentResponse } from '../types'
import {
  useTestRestrictions,
  TestLockedBadge,
  PaywallModal,
} from '@/features/payments'
import {
  trackUpgradeCtaClicked,
  trackLockedTestClicked,
  trackQuickFreeTestCtaClicked,
  trackFullFreeTestCtaClicked,
} from '@/shared/utils/analytics'

interface AssessmentCardProps {
  assessment: AssessmentResponse
  onTakeTest?: (testCode: string) => void
}

const AssessmentCard = ({ assessment, onTakeTest }: AssessmentCardProps) => {
  const [showPaywall, setShowPaywall] = useState(false)
  const { isTestLocked } = useTestRestrictions(assessment.testCode)
  const getTestTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      FULL_LENGTH: 'Full Length Test',
      SUBJECT_BASED: 'Subject Based',
      MINOR: 'Minor Test',
      STRATEGIC: 'Strategic Test',
      ACTUAL_EXAM: 'Actual Exam',
      QUICK_DIAGNOSTIC: 'Quick Diagnostic',
    }
    return labels[type] || type
  }

  const getTestTypeColors = (type: string) => {
    const colors: Record<string, string> = {
      FULL_LENGTH: 'bg-indigo-100 text-indigo-800',
      SUBJECT_BASED: 'bg-emerald-100 text-emerald-800',
      MINOR: 'bg-amber-100 text-amber-800',
      STRATEGIC: 'bg-purple-100 text-purple-800',
      ACTUAL_EXAM: 'bg-rose-100 text-rose-800',
      QUICK_DIAGNOSTIC: 'bg-cyan-100 text-cyan-800',
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const getTestNameColor = (type: string) => {
    const colors: Record<string, string> = {
      FULL_LENGTH: 'text-indigo-700',
      SUBJECT_BASED: 'text-emerald-700',
      MINOR: 'text-amber-700',
      STRATEGIC: 'text-purple-700',
      ACTUAL_EXAM: 'text-rose-700',
      QUICK_DIAGNOSTIC: 'text-cyan-700',
    }
    return colors[type] || 'text-gray-900'
  }

  const getStatsBgColor = (type: string) => {
    const colors: Record<string, string> = {
      FULL_LENGTH: 'bg-indigo-50',
      SUBJECT_BASED: 'bg-emerald-50',
      MINOR: 'bg-amber-50',
      STRATEGIC: 'bg-purple-50',
      ACTUAL_EXAM: 'bg-rose-50',
      QUICK_DIAGNOSTIC: 'bg-cyan-50',
    }
    return colors[type] || 'bg-gray-50'
  }

  const getTagColors = (type: string) => {
    const colors: Record<string, string> = {
      FULL_LENGTH: 'bg-indigo-50 text-indigo-700',
      SUBJECT_BASED: 'bg-emerald-50 text-emerald-700',
      MINOR: 'bg-amber-50 text-amber-700',
      STRATEGIC: 'bg-purple-50 text-purple-700',
      ACTUAL_EXAM: 'bg-rose-50 text-rose-700',
      QUICK_DIAGNOSTIC: 'bg-cyan-50 text-cyan-700',
    }
    return colors[type] || 'bg-gray-50 text-gray-700'
  }

  const getButtonColors = (type: string) => {
    const colors: Record<string, string> = {
      FULL_LENGTH: 'bg-indigo-600 text-white hover:bg-indigo-700',
      SUBJECT_BASED: 'bg-emerald-600 text-white hover:bg-emerald-700',
      MINOR: 'bg-amber-600 text-white hover:bg-amber-700',
      STRATEGIC: 'bg-purple-600 text-white hover:bg-purple-700',
      ACTUAL_EXAM: 'bg-rose-600 text-white hover:bg-rose-700',
      QUICK_DIAGNOSTIC: 'bg-cyan-600 text-white hover:bg-cyan-700',
    }
    return colors[type] || 'bg-green-600 text-white hover:bg-green-700'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const isTestAvailable = () => {
    const now = new Date()
    const availableFrom = assessment.availableFrom ? new Date(assessment.availableFrom) : null
    const availableUntil = assessment.availableUntil ? new Date(assessment.availableUntil) : null

    // If both dates are provided, check the range
    if (availableFrom && availableUntil) {
      return now >= availableFrom && now <= availableUntil
    }

    // If only availableFrom is provided
    if (availableFrom && !availableUntil) {
      return now >= availableFrom
    }

    // If only availableUntil is provided
    if (!availableFrom && availableUntil) {
      return now <= availableUntil
    }

    // If neither is provided, test is available
    return true
  }

  const testAvailable = isTestAvailable()

  // Check if availableFrom is a future date
  const isAvailableFromFuture = () => {
    if (!assessment.availableFrom) return false
    const availableFrom = new Date(assessment.availableFrom)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    availableFrom.setHours(0, 0, 0, 0)
    return availableFrom > today
  }

  const availableFromIsFuture = isAvailableFromFuture()

  // Show Available From only if the test is currently available
  const showAvailableFrom = assessment.availableFrom && testAvailable

  // Show Available Until whenever it exists
  const showAvailableUntil = !!assessment.availableUntil

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="mb-4">
        <h3 className={`text-xl font-semibold ${getTestNameColor(assessment.testType)}`}>
          {assessment.testName}
        </h3>
      </div>

      {/* Description */}
      {assessment.description && (
        <div
          className="text-gray-700 mb-4 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: assessment.description }}
        />
      )}

      {/* Test Type Badge */}
      <div className="mb-4 flex items-center gap-2">
        <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded ${getTestTypeColors(assessment.testType)}`}>
          {getTestTypeLabel(assessment.testType)}
        </span>
        {isTestLocked && <TestLockedBadge />}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className={`${getStatsBgColor(assessment.testType)} rounded p-3`}>
          <p className="text-xs text-gray-600 mb-1">Duration</p>
          <p className={`text-lg font-semibold ${getTestNameColor(assessment.testType)}`}>
            {assessment.durationMinutes} min
          </p>
        </div>
        <div className={`${getStatsBgColor(assessment.testType)} rounded p-3`}>
          <p className="text-xs text-gray-600 mb-1">Total Marks</p>
          <p className={`text-lg font-semibold ${getTestNameColor(assessment.testType)}`}>{assessment.totalMarks}</p>
        </div>
        <div className={`${getStatsBgColor(assessment.testType)} rounded p-3`}>
          <p className="text-xs text-gray-600 mb-1">Questions</p>
          <p className={`text-lg font-semibold ${getTestNameColor(assessment.testType)}`}>{assessment.totalQuestions}</p>
        </div>
        <div className={`${getStatsBgColor(assessment.testType)} rounded p-3`}>
          <p className="text-xs text-gray-600 mb-1">Passing Marks</p>
          <p className={`text-lg font-semibold ${getTestNameColor(assessment.testType)}`}>{assessment.passingMarks}</p>
        </div>
      </div>

      {/* Additional Info */}
      <div className="space-y-2 mb-4">
        {assessment.negativeMarkingRatio > 0 && (
          <div className="flex items-center text-sm">
            <span className="text-gray-600 mr-2">Negative Marking:</span>
            <span className="font-medium text-red-600">
              -{assessment.negativeMarkingRatio}
            </span>
          </div>
        )}
        {showAvailableFrom && (
          <div className="flex items-center text-sm">
            <span className="text-gray-600 mr-2">Available From:</span>
            <span className="font-medium text-gray-900">
              {formatDate(assessment.availableFrom)}
            </span>
          </div>
        )}
        {showAvailableUntil && (
          <div className="flex items-center text-sm">
            <span className="text-gray-600 mr-2">Available Until:</span>
            <span className="font-medium text-gray-900">
              {formatDate(assessment.availableUntil)}
            </span>
          </div>
        )}
      </div>

      {/* Tags */}
      {assessment.tags && assessment.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {assessment.tags.map((tag, index) => (
            <span
              key={index}
              className={`inline-block text-xs px-2 py-1 rounded ${getTagColors(assessment.testType)}`}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Action Button */}
      {onTakeTest && (
        <>
          <button
            onClick={() => {
              if (isTestLocked) {
                trackLockedTestClicked(assessment.testName)
                trackUpgradeCtaClicked('test_series')
                setShowPaywall(true)
              } else {
                // Track free test CTA clicks based on test type
                if (assessment.testType === 'QUICK_DIAGNOSTIC') {
                  trackQuickFreeTestCtaClicked()
                } else if (assessment.testType === 'FULL_LENGTH') {
                  trackFullFreeTestCtaClicked()
                }
                onTakeTest(assessment.testCode)
              }
            }}
            disabled={!testAvailable && !isTestLocked}
            className={`w-full py-2 px-4 rounded transition-colors font-medium ${
              isTestLocked
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : testAvailable
                ? getButtonColors(assessment.testType)
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isTestLocked
              ? 'Unlock to Take Test'
              : testAvailable
                ? 'Take Test'
                : availableFromIsFuture
                  ? `Available from ${formatDate(assessment.availableFrom)}`
                  : 'Not Available'}
          </button>

          {/* Paywall Modal */}
          <PaywallModal
            isOpen={showPaywall}
            onClose={() => setShowPaywall(false)}
            triggerLocation="test_locked"
          />
        </>
      )}
    </div>
  )
}

export default AssessmentCard
